import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LogOut, Loader2, ChevronLeft, ChevronRight, Mail, Gift, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { adminLogin, adminMagic, adminRequestLink, adminList, adminApprove, adminDeny, adminUpdate, adminDelete, adminRefund, adminDenyRefund, getConfig } from "@/lib/api";
import { humanSlot } from "@/data/collections";
import { DetailsModal, StatusPill, DepositPill, DonePill } from "@/components/AdminDetails";
import { ReferrerLink } from "@/components/ReferrerLink";
import { DayPanel } from "@/components/DayPanel";
import { WeekView, startOfWeek } from "@/components/WeekView";
import { PriorityRemovals } from "@/components/PriorityRemovals";
import { toast } from "sonner";

const TOKEN_KEY = "pp_admin_token";

export default function Admin() {
  const [sp, setSp] = useSearchParams();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [exchanging, setExchanging] = useState(Boolean(sp.get("magic")));

  useEffect(() => {
    const magic = sp.get("magic");
    if (!magic) return;
    adminMagic(magic)
      .then((r) => { localStorage.setItem(TOKEN_KEY, r.token); setToken(r.token); toast.success("Signed in"); })
      .catch((e) => toast.error(e?.response?.data?.detail || "This link has expired"))
      .finally(() => { setExchanging(false); setSp({}, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (exchanging) {
    return (
      <div className="min-h-screen"><Header />
        <div className="pp-container py-32 text-center" style={{ color: "var(--pp-text-muted)" }}>
          <Loader2 className="h-6 w-6 animate-spin inline" /> <span className="ml-2">Signing you in…</span>
        </div>
      </div>
    );
  }
  return token ? (
    <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />
  ) : (
    <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [usePassword, setUsePassword] = useState(false);

  const sendLink = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email");
    setLoading(true);
    try { await adminRequestLink(email.trim()); setSent(true); }
    catch { toast.error("Could not send link"); }
    finally { setLoading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { onLogin((await adminLogin(email.trim(), password)).token); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="pp-container py-20 sm:py-28">
        <div className="max-w-md mx-auto">
          <div className="pp-caption mb-3">Owner sign in</div>
          <h1 className="font-serif-display text-5xl font-light mb-8" style={{ color: "var(--pp-burgundy)" }}>Welcome back.</h1>
          {sent ? (
            <div className="border p-6 text-sm" data-testid="admin-link-sent" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
              <Mail className="h-5 w-5 mb-3" style={{ color: "var(--pp-terracotta)" }} />
              If that's the owner email, a sign-in link is on its way. Open it on any device to manage bookings.
            </div>
          ) : (
            <form onSubmit={usePassword ? submit : sendLink} className="space-y-4 border p-6" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
              <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} />
              {usePassword && (
                <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} />
              )}
              <button type="submit" data-testid={usePassword ? "admin-login-button" : "admin-send-link-button"} disabled={loading} className="pp-btn w-full">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {usePassword ? "Sign in" : "Email me a sign-in link"}
              </button>
              <button type="button" data-testid="admin-toggle-password" onClick={() => setUsePassword((v) => !v)} className="w-full text-center text-xs underline underline-offset-4" style={{ color: "var(--pp-text-muted)" }}>
                {usePassword ? "Use an email link instead" : "Sign in with password instead"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
const inp = "w-full bg-[var(--pp-cream)] px-4 py-3 text-sm outline-none border-b border-[var(--pp-border)] focus:border-[var(--pp-terracotta)]";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [filter, setFilter] = useState("all");
  const [hideDone, setHideDone] = useState(false);
  const [sandbox, setSandbox] = useState(false);
  useEffect(() => { getConfig().then((c) => setSandbox(Boolean(c.stripe_sandbox))).catch(() => {}); }, []);
  const [sort, setSort] = useState("install");
  const [query, setQuery] = useState("");
  const [day, setDay] = useState(null);
  const [view, setView] = useState("month");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const load = () => {
    setLoading(true);
    adminList(token)
      .then((r) => setBookings(r.bookings || []))
      .catch((e) => { if (e?.response?.status === 401) onLogout(); else toast.error("Failed to load bookings"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = filter === "all" ? bookings : filter === "priority" ? bookings.filter((b) => b.priority_removal && b.status !== "denied") : filter === "referrals" ? bookings.filter((b) => b.referred_by && b.status !== "denied") : filter === "completed" ? bookings.filter((b) => b.completed) : bookings.filter((b) => b.status === filter);
    if (hideDone && filter !== "completed") list = list.filter((b) => !b.completed);
    if (q) list = list.filter((b) => [b.full_name, b.email, b.reference, b.address, b.referred_by].join(" ").toLowerCase().includes(q));
    const key = sort === "install" ? (b) => `${b.date} ${b.time_slot}` : (b) => b.created_at;
    return [...list].sort((a, b) => (sort === "newest" ? key(b).localeCompare(key(a)) : key(a).localeCompare(key(b))));
  }, [bookings, filter, sort, query, hideDone]);

  const priorityCount = useMemo(() => bookings.filter((b) => b.priority_removal && b.status !== "denied").length, [bookings]);
  const byDate = useMemo(() => {
    const m = {};
    for (const b of bookings) (m[b.date] ||= []).push(b);
    return m;
  }, [bookings]);

  const act = (fn, msg) => async (...args) => {
    try { await fn(token, ...args); toast.success(msg); load(); setSelected(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); throw e; }
  };
  const refund = async (id, amount, reason) => {
    try {
      const r = await adminRefund(token, id, amount, reason);
      toast.success(`Refunded $${Number(r.refund.amount).toLocaleString()}`);
      setSelected((prev) => (prev ? r.booking : prev)); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); throw e; }
  };
  const toggle = async (id, patch) => {
    try {
      const r = await adminUpdate(token, id, patch);
      toast.success("deposit_paid" in patch ? (patch.deposit_paid ? "Marked paid" : "Marked unpaid") : "referral_credited" in patch ? (patch.referral_credited ? "$50 referral credits marked issued" : "Referral credits reopened") : "removal_completed" in patch ? (patch.removal_completed ? "Removal marked done" : "Removal reopened") : (patch.completed ? "Install marked complete" : "Install reopened"));
      setSelected((prev) => (prev ? r.booking : prev)); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); throw e; }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="pp-container py-10 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="pp-caption mb-1">Owner dashboard</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: "var(--pp-burgundy)" }}>Booking requests</h1>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="pp-btn-ghost" style={{ color: "var(--pp-text-muted)" }}>
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="flex items-center justify-end gap-1 mb-3">
              {["month", "week", "removals"].map((v) => (
                <button key={v} data-testid={`admin-view-${v}`} onClick={() => setView(v)}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors"
                  style={{ backgroundColor: view === v ? "var(--pp-burgundy)" : "transparent", color: view === v ? "var(--pp-parchment)" : "var(--pp-text-muted)", border: "1px solid var(--pp-border)" }}>
                  {v === "removals" ? `Priority Removal (${priorityCount})` : v}
                </button>
              ))}
            </div>
            {view === "removals" ? (
              <PriorityRemovals bookings={bookings} onSelect={setSelected} onToggle={toggle} />
            ) : view === "week" ? (
              <WeekView weekStart={weekStart} setWeekStart={setWeekStart} byDate={byDate} onSelect={setSelected} selectedDay={day} onSelectDay={setDay} />
            ) : (
              <MonthCalendar month={month} setMonth={setMonth} byDate={byDate} onSelect={setSelected} selectedDay={day} onSelectDay={setDay} />
            )}
          </div>

          <div className="lg:col-span-5">
            {day ? (
              <DayPanel date={day} bookings={byDate[day] || []} onClose={() => setDay(null)} onSelect={setSelected} onToggle={toggle} />
            ) : (
            <div className="border" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
              <div className="p-4 border-b space-y-3" style={{ borderColor: "var(--pp-border)" }}>
                <div className="flex flex-wrap gap-1">
                  {["all", "pending", "approved", "denied", "priority", "referrals", "completed"].map((k) => (
                    <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)}
                      className="px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors"
                      style={{ backgroundColor: filter === k ? "var(--pp-burgundy)" : "transparent", color: filter === k ? "var(--pp-parchment)" : "var(--pp-text-muted)" }}>
                      {k}
                    </button>
                  ))}
                  <label className="ml-auto flex items-center gap-1.5 text-[11px] uppercase tracking-widest cursor-pointer" style={{ color: "var(--pp-text-muted)" }}>
                    <input type="checkbox" data-testid="admin-hide-completed" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} /> Hide completed
                  </label>
                </div>
                <div className="flex gap-2">
                  <input data-testid="admin-search-input" placeholder="Search name, email, reference…" value={query} onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 bg-[var(--pp-cream)] px-3 py-2 text-sm outline-none" />
                  <select data-testid="admin-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[var(--pp-cream)] px-2 py-2 text-xs outline-none">
                    <option value="install">Install date ↑</option>
                    <option value="newest">Newest requests</option>
                    <option value="oldest">Oldest requests</option>
                  </select>
                </div>
              </div>
              {loading ? (
                <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-sm" style={{ color: "var(--pp-text-muted)" }}>No requests match.</div>
              ) : (
                <ul className="max-h-[560px] overflow-auto divide-y" style={{ borderColor: "var(--pp-border)" }}>
                  {filtered.map((b) => (
                    <li key={b.id}>
                      <button data-testid={`admin-booking-row-${b.reference}`} onClick={() => setSelected(b)} className="w-full text-left p-4 hover:bg-[var(--pp-cream)] transition-colors">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-serif-display text-xl flex items-center gap-2" style={{ color: "var(--pp-burgundy)" }}>{b.reference}{b.is_gift && <Gift className="h-4 w-4" style={{ color: "var(--pp-terracotta)" }} />}{b.referred_by && <Users className="h-4 w-4" title={`Referred by ${b.referred_by}`} style={{ color: b.referral_credited ? "var(--pp-sage)" : "#B87333" }} />}</div>
                          <div className="flex items-center gap-1.5">{b.completed && <DonePill />}<DepositPill paid={b.deposit_paid} /><StatusPill status={b.status} /></div>
                        </div>
                        <div className="mt-1 text-sm">{b.full_name} · {b.collection_name} · {b.size_label}</div>
                        <div className="text-xs" style={{ color: "var(--pp-text-muted)" }}>{b.date} · {humanSlot(b.time_slot)}{b.priority_removal ? " · Priority Removal" : ""}</div>
                        {filter === "priority" && <div className="text-xs mt-1" style={{ color: "var(--pp-text-muted)" }}>{b.address} · {b.email} · {b.phone}</div>}
                        {filter === "referrals" && <div className="text-xs mt-1" data-testid={`admin-referral-line-${b.reference}`} style={{ color: b.referral_credited ? "var(--pp-sage)" : "#B87333" }}>Referred by <ReferrerLink name={b.referred_by} bookings={bookings} excludeId={b.id} onSelect={setSelected} testId={`admin-referrer-link-${b.reference}`} /> · {b.referral_credited ? "$50 credits issued" : "$50 credits pending"}</div>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}
          </div>
        </div>
      </section>

      {selected && (
        <DetailsModal booking={selected} onClose={() => setSelected(null)}
          onApprove={act(adminApprove, "Approved — customer notified")}
          onDeny={act(adminDeny, "Denied — customer notified")}
          onDenyRefund={act(adminDenyRefund, "Cancelled & refunded — customer notified")}
          onSave={act(adminUpdate, "Booking updated")}
          onDelete={act(adminDelete, "Booking deleted")}
          onToggle={toggle} onRefund={refund} sandbox={sandbox} allBookings={bookings} onOpenBooking={setSelected} />
      )}
    </div>
  );
}

function MonthCalendar({ month, setMonth, byDate, onSelect, selectedDay, onSelectDay }) {
  const startDow = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (cells.length % 7) cells.push(null);
  const tone = { approved: ["#E1E8D8", "#3F5B33"], denied: ["#EED6D2", "#8A2E2E"], pending: ["#F4E3C8", "#8A5A20"], pending_payment: ["#F4E3C8", "#8A5A20"] };
  const toneFor = (s) => tone[s] || tone.pending;

  return (
    <div className="border p-4 sm:p-6" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-9 w-9 inline-flex items-center justify-center hover:bg-[var(--pp-cream)]" data-testid="admin-cal-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-serif-display text-2xl" style={{ color: "var(--pp-burgundy)" }}>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-9 w-9 inline-flex items-center justify-center hover:bg-[var(--pp-cream)]" data-testid="admin-cal-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--pp-text-muted)" }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          const items = byDate[ymd(c)] || [];
          const key = ymd(c);
          const active = selectedDay === key;
          return (
            <div key={i} role="button" tabIndex={0} data-testid={`admin-cal-day-${key}`}
              onClick={() => onSelectDay(active ? null : key)}
              onKeyDown={(e) => e.key === "Enter" && onSelectDay(active ? null : key)}
              className="min-h-[76px] p-1.5 border cursor-pointer transition-colors hover:bg-[var(--pp-parchment)]"
              style={{ borderColor: active ? "var(--pp-terracotta)" : "var(--pp-border)", backgroundColor: active ? "#F6E6D4" : c.getDay() === 0 ? "var(--pp-parchment)" : "var(--pp-cream)", outline: active ? "1px solid var(--pp-terracotta)" : "none" }}>
              <div className="text-xs mb-1 flex items-center justify-between" style={{ color: active ? "var(--pp-terracotta)" : "var(--pp-text-muted)" }}>
                <span>{c.getDate()}</span>
                {items.length > 0 && <span className="text-[10px]">{items.length}</span>}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((b) => (
                  <button key={b.id} onClick={(e) => { e.stopPropagation(); onSelectDay(key); onSelect(b); }} className="block w-full text-left text-[10px] px-1.5 py-0.5 truncate"
                    style={{ backgroundColor: toneFor(b.status)[0], color: toneFor(b.status)[1] }}>
                    {humanSlot(b.time_slot)} · {b.full_name.split(" ")[0]}
                  </button>
                ))}
                {items.length > 3 && <div className="text-[10px]" style={{ color: "var(--pp-text-muted)" }}>+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
